import {RootPopup} from "@/components/layouts/popup/RootPopup";
import {FormError} from "@/components/ui/forms/FormError";
import {
  setEmail,
  setError,
  setPassword,
  signIn,
  toggleConsent,
} from "@/redux/slices/commonSlices/candidateSlice";

import {useEffect, useRef} from "react";

import {isValidEmail} from "@helpers";
import fallbackImage from "@images/general/video-fallback.png";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {CallPopup} from "@popups/requestPopups/CallPopup";
import {HelpPopup} from "@popups/requestPopups/HelpPopup";
import {PresentationPopup} from "@popups/requestPopups/PresentationPopup";
import {ControlButton} from "@ui/buttons/ControlButton";
import {RootButton} from "@ui/buttons/RootButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {Spinner} from "@ui/general/Spinner";
import {RootCheckbox} from "@ui/inputs/RootCheckbox";
import {RootInput} from "@ui/inputs/RootInput";
import {Helmet} from "react-helmet";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {Link, useNavigate} from "react-router-dom";

import styles from "./styles.module.scss";

// раскоментировать для теста видео
// import videoMp4 from '@/assets/video.mp4'
// import videoMov from '@/assets/video.mov'
// import videoWebm from '@/assets/video.webm'

export const Login = () => {
  // закоментировать для теста видео
  const videoMov = "/video/video.mov";
  const videoWebm = "/video/video.webm";
  const videoMp4 = "/video/video.mp4";

  const {isMobile, navigateHistory} = useSelector(
    (state) => state.common.settings,
  );
  const {email, password, consent, error, serverStatus} = useSelector(
    (state) => state.common.candidate,
  );
  const {openPopup} = usePopup();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const videoRef = useRef(null);

  const handleOpenPopup = (popup) => {
    openPopup(popup);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(setError(""));

    if (!isValidEmail(email) || !password) {
      return dispatch(setError(t("server_error.login")));
    } else if (!consent) {
      return dispatch(setError(t("server_error.consent")));
    }

    const resultAction = await dispatch(signIn({email, password}));

    if (signIn.fulfilled.match(resultAction)) {
      const userRole = resultAction.payload.type;

      if (userRole === "Admin" || userRole === "Manager") {
        navigate(
          navigateHistory && navigateHistory.includes("admin-panel")
            ? navigateHistory
            : "/admin-panel/dashboard",
        );
      } else {
        navigate(
          navigateHistory && navigateHistory.includes("user-panel")
            ? navigateHistory
            : "/simulations/call",
        );
      }
    } else {
      dispatch(setError(t("server_error.login")));
    }
  };

  const buttonList = [
    {
      id: 0,
      name: t("page.login.popup_links.call"),
      iconId: "call",
      popup: <CallPopup />,
    },
    {
      id: 1,
      name: t("page.login.popup_links.presentation"),
      iconId: "request",
      popup: <PresentationPopup />,
    },
    {
      id: 2,
      name: t("page.login.popup_links.help"),
      iconId: "question",
      popup: <HelpPopup />,
    },
  ];

  useEffect(() => {
    dispatch(setError(""));
  }, [email, password, consent]);

  useEffect(() => {
    if (!isMobile) {
      const videoElement = videoRef?.current;

      if (videoElement) {
        const handleTimeUpdate = () => {
          if (
            Math.round(videoElement?.currentTime) >=
            Math.round(videoElement?.duration) - 1
          ) {
            videoElement.pause();
          }
        };

        videoElement.addEventListener("timeupdate", handleTimeUpdate);
        videoElement.play();

        return () => {
          videoElement.removeEventListener("timeupdate", handleTimeUpdate);
        };
      }
    }
  }, [isMobile]);

  return (
    <>
      <Helmet title={t("head_title.login")} />

      <div className={styles.loginWrapper}>
        <div className={"container-big"}>
          <div className={styles.login}>
            {/* <Logo /> */}

            <div className={styles.loginContent}>
              <h1
                dangerouslySetInnerHTML={{
                  __html: t("page.login.title"),
                }}
              />

              <h2
                dangerouslySetInnerHTML={{
                  __html: t("page.login.subtitle"),
                }}
              />

              <div className={styles.loginPopup}>
                <RootPopup width={440}>
                  <div className={styles.content}>
                    <RootDesc>
                      <span
                        className={"inline-block w-[100%] text-center"}
                        dangerouslySetInnerHTML={{
                          __html: t("page.login.popup_subtitle"),
                        }}
                      />
                    </RootDesc>

                    <form
                      onSubmit={handleSubmit}
                      className={styles.form}
                    >
                      <div className={styles.inputs}>
                        <div className={styles.input}>
                          <RootInput
                            width={220}
                            label={t("input.login")}
                            type={"text"}
                            value={email}
                            require
                            onChange={(e) => dispatch(setEmail(e.target.value))}
                            disabled={serverStatus === "loading"}
                            tabIndex={1}
                          />
                        </div>

                        <div className={styles.input}>
                          <RootInput
                            width={220}
                            label={t("input.password")}
                            type={"password"}
                            value={password}
                            require
                            onChange={(e) =>
                              dispatch(setPassword(e.target.value))
                            }
                            disabled={serverStatus === "loading"}
                            tabIndex={2}
                          />
                        </div>
                      </div>

                      <div className={styles.loginCheckbox}>
                        <RootCheckbox
                          disabled={serverStatus === "loading"}
                          checked={consent}
                          onChange={() => dispatch(toggleConsent())}
                          tabIndex={3}
                          label={
                            <>
                              {t("input.privacy_checkbox_label")}{" "}
                              <Link to={"/privacy"}>
                                {t("input.privacy_checkbox_link")}
                              </Link>
                            </>
                          }
                        />
                      </div>

                      <RootButton
                        type={"submit"}
                        txt={<span>{t("page.login.popup_button")}</span>}
                        disabled={
                          !email ||
                          !password ||
                          !consent ||
                          serverStatus === "loading"
                        }
                        tabIndex={4}
                      />

                      {error !== "" && (
                        <FormError
                          error={error}
                          bottom={105}
                          center
                        />
                      )}

                      {serverStatus === "loading" && (
                        <Spinner
                          width={50}
                          height={50}
                        />
                      )}
                    </form>
                  </div>
                </RootPopup>

                <ul className={styles.loginButtons}>
                  {buttonList.map((btn) => (
                    <li key={btn.id}>
                      <ControlButton
                        color={"var(--black)"}
                        txt={btn.name}
                        iconId={btn.iconId}
                        onClick={() => handleOpenPopup(btn.popup)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {!isMobile && (
          <div className={styles.loginVideoWrapper}>
            <div className={"container-big"}>
              <div className={styles.loginVideoMask}>
                <div className={styles.loginVideo}>
                  {videoMp4 && videoWebm && videoMov && (
                    <video
                      ref={videoRef}
                      autoPlay
                      loop={false}
                      muted
                      width={"1840rem"}
                      height={"900rem"}
                      poster={fallbackImage}
                    >
                      <source
                        src={videoMov}
                        type={"video/quicktime"}
                      />
                      <source
                        src={videoWebm}
                        type={"video/webm"}
                      />
                      <source
                        src={videoMp4}
                        type={"video/mp4"}
                      />
                    </video>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
